<template>
  <section class="cs">
    <h2 class="cs__title">
      💬 Commentaires
      <span v-if="comments.length" class="cs__count">{{ comments.length }}</span>
    </h2>

    <div v-if="$apollo.queries.comments.loading && !comments.length" class="cs__state">
      <span class="spin">🍌</span> Chargement des commentaires…
    </div>

    <ul v-else-if="comments.length" class="cs__list">
      <li v-for="c in comments" :key="c.id" class="cs__item" :class="{ 'cs__item--removed': c.isRemoved }">
        <template v-if="c.isRemoved">
          <p class="cs__removed">
            🚫 Ce commentaire a été supprimé par les modérateurs pour&nbsp;: {{ c.removedReason }}
          </p>
        </template>
        <template v-else>
          <div class="cs__meta">
            <span class="cs__author">✍️ {{ c.authorName }}</span>
            <span class="cs__date">{{ formatDate(c.createdAt) }}</span>
            <button class="cs__report" type="button" @click="openReport(c.id)">Signaler</button>
          </div>
          <p class="cs__text">{{ c.text }}</p>
        </template>
      </li>
    </ul>

    <p v-else class="cs__empty">Sois le premier à réagir à cette blague 🍌</p>

    <!-- Add form -->
    <div class="cs__add">
      <template v-if="isAuth">
        <form @submit.prevent="submit">
          <textarea
            v-model="draft"
            class="cs__textarea"
            rows="3"
            placeholder="Ajoute ton grain de sel…"
            maxlength="2000"
          ></textarea>
          <p v-if="addError" class="cs__error">{{ addError }}</p>
          <div class="cs__add-actions">
            <BananaButton type="submit" size="sm" :loading="adding" :disabled="!draft.trim()">
              Publier
            </BananaButton>
          </div>
        </form>
      </template>
      <template v-else>
        <div class="cs__login">
          <p>Connecte-toi pour commenter cette blague.</p>
          <BananaButton variant="dark" size="sm" :to="loginTo">Se connecter</BananaButton>
        </div>
      </template>
    </div>

    <ReportDialog
      v-if="reportCommentId !== null"
      target="comment"
      :target-id="reportCommentId"
      @close="reportCommentId = null"
    />
  </section>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import ReportDialog from '@/components/ReportDialog.vue'
import { COMMENTS_QUERY, ADD_COMMENT_MUTATION } from '@/graphql/social'
import { isAuthenticated } from '@/auth'

export default {
  name: 'CommentSection',
  components: { BananaButton, ReportDialog },
  props: {
    jokeId: { type: Number, required: true },
  },
  data() {
    return {
      comments: [],
      draft: '',
      adding: false,
      addError: '',
      reportCommentId: null,
    }
  },
  computed: {
    isAuth() {
      return isAuthenticated()
    },
    loginTo() {
      return { path: '/login', query: { redirect: this.$route.fullPath } }
    },
  },
  apollo: {
    comments: {
      query: COMMENTS_QUERY,
      variables() {
        return { jokeId: this.jokeId }
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toLocaleDateString('fr-FR', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
      } catch {
        return ''
      }
    },
    openReport(id) {
      this.reportCommentId = parseInt(id, 10)
    },
    async submit() {
      const text = this.draft.trim()
      if (!text || this.adding) return
      this.addError = ''
      this.adding = true
      try {
        await this.$apollo.mutate({
          mutation: ADD_COMMENT_MUTATION,
          variables: { jokeId: this.jokeId, text },
        })
        this.draft = ''
        await this.$apollo.queries.comments.refetch()
      } catch (e) {
        const raw = (e && e.message) ? e.message : ''
        this.addError = raw.replace(/^GraphQL error:\s*/i, '') || 'Impossible de publier le commentaire.'
      } finally {
        this.adding = false
      }
    },
  },
}
</script>

<style scoped>
.cs {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: 22px 22px 20px;
}
.cs__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.4rem;
  margin: 0 0 16px;
}
.cs__count {
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--ink-900);
  background: var(--banana-500);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  padding: 1px 10px;
}
.cs__state, .cs__empty {
  color: var(--text-soft);
  font-family: var(--font-accent);
  font-style: italic;
  padding: 8px 0 16px;
}
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.cs__list { list-style: none; margin: 0 0 18px; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.cs__item {
  border: 1.5px solid var(--line);
  border-radius: var(--r-md);
  padding: 12px 14px;
  background: var(--banana-50);
}
.cs__item--removed { background: rgba(26, 18, 7, 0.04); border-style: dashed; }
.cs__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.cs__author { font-family: var(--font-display); font-weight: 700; font-size: 0.9rem; color: var(--ink-800); }
.cs__date { font-size: 0.78rem; color: var(--text-soft); }
.cs__report {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--bruise);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--r-sm);
  transition: background 0.15s;
}
.cs__report:hover { background: rgba(226, 78, 27, 0.12); }
.cs__text {
  margin: 0;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--ink-900);
}
.cs__removed {
  margin: 0;
  font-style: italic;
  color: var(--ink-300);
  font-size: 0.92rem;
}

.cs__add { border-top: 1.5px dashed var(--line); padding-top: 16px; }
.cs__textarea {
  width: 100%;
  font-family: var(--font-body);
  font-size: 0.98rem;
  padding: 10px 12px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-sm);
  background: var(--cream);
  color: var(--ink-900);
  resize: vertical;
}
.cs__textarea:focus { outline: 3px solid var(--banana-500); outline-offset: 1px; }
.cs__add-actions { display: flex; justify-content: flex-end; margin-top: 10px; }
.cs__error { color: var(--bruise); font-weight: 600; font-size: 0.88rem; margin: 8px 0 0; }
.cs__login {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.cs__login p { margin: 0; color: var(--text-soft); }

@media (max-width: 560px) {
  .cs { padding: 18px 15px 16px; border-radius: var(--r-md); }
  .cs__title { font-size: 1.2rem; }
}
</style>
